'use client';

import {
  IconButton as ChakraIconButton,
  IconButtonProps as ChakraIconButtonProps,
  defineStyleConfig,
  forwardRef,
  useStyleConfig
} from '@chakra-ui/react';
import { useState } from 'react';

import Icon, { iconRecord } from '@/components/atomic/v1/icon/Icon';

interface IconButtonProps extends ChakraIconButtonProps {
  iconName: keyof typeof iconRecord;
  variant: 'yellow' | 'green' | 'blue' | 'purple';
}

export const iconButtonTheme = defineStyleConfig({
  baseStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'auto',
    minWidth: 'auto',
    height: '48px',
    width: '48px',
    _hover: {
      cursor: 'pointer'
    },
    _active: {
      'svg g path': {
        fill: 'icon/default'
      }
    },
    _focus: {
      outlineColor: 'icon/hover'
    }
  },
  variants: {
    yellow: {
      backgroundColor: 'status/warning/bg',
      _active: {
        backgroundColor: 'status/warning/bg/active'
      }
    },
    green: {
      backgroundColor: 'status/success/bg',
      _active: {
        backgroundColor: 'status/success/bg/active'
      }
    },
    blue: {
      backgroundColor: 'status/information/bg',
      _active: {
        backgroundColor: 'status/information/bg/active'
      }
    },
    purple: {
      backgroundColor: 'icon/purple/default',
      _active: {
        backgroundColor: 'icon/purple/default/active'
      }
    }
  },
  defaultProps: {
    variant: 'yellow'
  }
});
/**
 * Based on `<IconButton>` from ChakraUI component library
 *
 * https://chakra-ui.com/docs/components/button
 */

/**
 * @param iconName - The 24x24 icon which has outline and solid versions
 *
 */

const IconButton = forwardRef<IconButtonProps, 'button'>(({ iconName, variant, ...rest }, ref) => {
  const [isHovered, setIsHovered] = useState(false);
  const styles = useStyleConfig('IconButton', { variant });
  const isDisabled = rest.isDisabled || rest.disabled;

  const getIconColor = () => {
    if (isDisabled) {
      return 'icon/disabled';
    }

    if (isHovered) {
      return 'icon/hover';
    }

    return 'icon/default';
  };

  return (
    <ChakraIconButton
      data-testid={`icon-button-${iconName}`}
      icon={<Icon name={iconName} color={getIconColor()} kind={isHovered && !isDisabled ? 'solid' : 'outline'} />}
      isRound
      __css={{
        ...styles
      }}
      {...rest}
      onMouseOver={() => !isDisabled && setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      onFocus={() => !isDisabled && setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      ref={ref}
    />
  );
});

export default IconButton;
